import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { removeToast } from '../../store/slices/toastSlice';
import styles from './Toast.module.css';

/**
 * Individual Toast component
 * @param {Object} props
 * @param {string} props.id - Unique ID for the toast
 * @param {string} props.type - Toast type: 'success', 'error', 'warning', 'info'
 * @param {string} props.message - Message to display
 * @param {number} props.duration - Time in ms before toast auto-dismisses
 * @param {boolean} props.pauseOnHover - Pause progress when hovering
 * @param {boolean} props.dismissible - Allow manual dismissal
 */
const Toast = ({ 
  id, 
  type, 
  message, 
  duration, 
  pauseOnHover = true,
  dismissible = true 
}) => {
  const dispatch = useDispatch();
  const [progress, setProgress] = useState(100);
  const [isExiting, setIsExiting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const remainingTimeRef = useRef(duration);
  const lastUpdateTimeRef = useRef(Date.now());

  // Handle close toast
  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      dispatch(removeToast(id));
    }, 300); // Wait for exit animation
  };

  // Handle mouse enter/leave for pause on hover
  const handleMouseEnter = () => {
    if (pauseOnHover) {
      setIsPaused(true);
      // Save the remaining time when paused
      remainingTimeRef.current = (progress / 100) * duration;
    }
  };

  const handleMouseLeave = () => {
    if (pauseOnHover) {
      setIsPaused(false);
      // Update the last update time to now
      lastUpdateTimeRef.current = Date.now();
    }
  };

  // Auto-dismiss with progress bar
  useEffect(() => {
    // Initialize remaining time and last update time
    remainingTimeRef.current = duration;
    lastUpdateTimeRef.current = Date.now();
    
    // Update progress every 16ms (approximately 60fps)
    const timer = setInterval(() => {
      if (!isPaused) {
        const now = Date.now();
        const delta = now - lastUpdateTimeRef.current;
        lastUpdateTimeRef.current = now;
        
        // Update remaining time
        remainingTimeRef.current = Math.max(0, remainingTimeRef.current - delta);
        const newProgress = (remainingTimeRef.current / duration) * 100;
        
        setProgress(newProgress);
        
        if (newProgress <= 0) {
          clearInterval(timer);
          handleClose();
        }
      }
    }, 16);

    return () => clearInterval(timer);
  }, [duration, id, isPaused]);

  // Icon based on toast type
  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={styles.icon}>
            <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={styles.icon}>
            <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={styles.icon}>
            <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={styles.icon}>
            <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  return (
    <div 
      className={`${styles.toast} ${styles[type]} ${isExiting ? styles.exit : ''}`}
      role="alert"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.content}>
        <div className={styles.iconContainer}>
          {getIcon()}
        </div>
        <div className={styles.message}>{message}</div>
        {dismissible && (
          <button 
            onClick={handleClose}
            className={styles.closeButton}
            aria-label="Close toast"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
      <div className={styles.progressBar}>
        <div 
          className={styles.progress}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

export default Toast;
